// Components/Layout/Pages/EventAdmin/EventAdminDashboard.js
import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import useFileDownload from '../../../../commonFn/useFileDownload';

const EventAdminDashboard = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [showPaymentExportMenu, setShowPaymentExportMenu] = useState(false);
    const [loading, setLoading] = useState(true);
    const [eventData, setEventData] = useState(null);
    const [error, setError] = useState(null);
    const [showNoDataPopup, setShowNoDataPopup] = useState(false);
    const [attendeeData, setAttendeeData] = useState(null);
    const [showAttendeeModal, setShowAttendeeModal] = useState(false);
    const [loadingAttendee, setLoadingAttendee] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    const encryptionKey = process.env.REACT_APP_CRYPTO_SECRET || 'your-secret-key';

    // Create axios instance with base configuration
    const apiClient = axios.create({
        baseURL: process.env.REACT_APP_API_URL || 'https://events.psginstitutions.in/EMSAPI/api',
        headers: {
            "Content-Type": "application/json",
        },
    });
    // Add request interceptor to add auth token
    apiClient.interceptors.request.use(
        (config) => {
            const decryptValue = (encryptedValue) => {
                if (!encryptedValue) return null;
                const bytes = CryptoJS.AES.decrypt(encryptedValue, encryptionKey);
                return bytes.toString(CryptoJS.enc.Utf8);
            };

            const encryptedToken = Cookies.get('a_t');
            const token = decryptValue(encryptedToken);

            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Add response interceptor for handling common errors
    apiClient.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response?.status === 401) {
                Cookies.remove("a_t", { path: "/EMS" });

                Swal.fire({
                    title: "Session Expired",
                    text: "Your session has expired. Please login again.",
                    icon: "warning",
                    confirmButtonText: "OK",
                }).then(() => {
                    navigate(`/register/${eventId}/admin`);
                });
            }
            return Promise.reject(error);
        }
    );

    const { downloadFile: downloadPayDetails } = useFileDownload({
        url: `Report/GetEventWisePayDetails?EventId=${eventId}&format=`,
        defaultFilename: "PayDetails"
    });

    const { downloadFile: downloadAttendance } = useFileDownload({
        url: `Report/GetEventAttendance?EventId=${eventId}&format=`,
        defaultFilename: "Attendance"
    });

    // Individual participant download function
    const downloadIndividualPayDetails = async (format, email, participantId, eventId, regId) => {
        try {
            setDownloadingId(regId);

            const encodedEmail = encodeURIComponent(email);
            const response = await apiClient({
                url: `/Report/DownloadReceipt?email=${encodedEmail}&eventid=${eventId}&ParticipantID=${participantId}&format=${format}`,
                method: 'GET',
                responseType: 'blob',
            });

            // Create download link
            const blob = new Blob([response.data]);
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);

            // Set filename from content-disposition or create one
            const contentDisposition = response.headers['content-disposition'];
            let filename = `Payment_Details_${participantId}`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            } else {
                filename += format === 'pdf' ? '.pdf' : format === 'excel' ? '.xlsx' : '.csv';
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);

            Swal.fire({
                title: "Success!",
                text: "Payment details downloaded successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

        } catch (err) {
            console.error('Error downloading payment details:', err);

            if (err.response?.status === 401) {
                // Session expired - interceptor will handle navigation
                return;
            } else {
                Swal.fire({
                    title: "Download Failed",
                    text: "Failed to download payment details. Please try again.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } finally {
            setDownloadingId(null);
        }
    };

    // Handle individual download button click
    const handleIndividualDownload = (e, reg, format) => {
        e.stopPropagation(); // Prevent row click event
        downloadIndividualPayDetails(
            format,
            reg.email,
            reg.participantId,
            eventId,
            reg.regId
        );
    };

    useEffect(() => {
        fetchEventData();
    }, [eventId]);

    const handlePaymentReport = (format) => {
        downloadPayDetails(format);
    };

    const handleAttendenceExport = (format) => {
        downloadAttendance(format);
    };

    const fetchEventData = async () => {
        try {
            setLoading(true);
            setError(null);
            setShowNoDataPopup(false);

            const response = await apiClient.get(`/Event/Admin-screen-payment-details?eventId=${eventId}`);

            // Check if response is empty or has error structure
            if (!response.data) {
                throw new Error('No data received from server');
            }

            // Handle 404-like responses
            if (response.data.status === 404 || response.data.title === "Not Found") {
                setEventData(null);
                setShowNoDataPopup(true);
                return;
            }

            if (response.data.isError) {
                throw new Error(response.data.message);
            }

            if (!response.data.data) {
                setEventData(null);
                setShowNoDataPopup(true);
                return;
            }

            setEventData(response.data.data);
        } catch (err) {
            console.error('Error fetching event data:', err);

            // Handle 404 errors specifically
            if (err.response?.status === 404 || err.response?.data?.status === 404) {
                setEventData(null);
                setShowNoDataPopup(true);
                return;
            }

            if (err.response?.status === 401) {
                // Session expired - interceptor will handle navigation
                return;
            }

            setError(err.message);
            Swal.fire({
                title: "Error!",
                text: "Failed to load event data. Please try again.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setLoading(false);
        }
    };

    // Show no data popup when showNoDataPopup is true
    useEffect(() => {
        if (showNoDataPopup && !loading) {
            Swal.fire({
                title: 'No Data Found',
                text: 'No event data is available for this event ID.',
                icon: 'info',
                confirmButtonText: 'OK',
            });
        }
    }, [showNoDataPopup, loading]);

    const handleBack = () => {
        document.cookie = "a_t=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
        navigate(`/register/${eventId}/admin`);
    }

    // Transform API data to match component structure
    const transformedEvent = useMemo(() => {
        if (!eventData) {
            // Return empty structure for no data
            return {
                Event_Id: eventId,
                Event_Name: 'Event Not Found',
                Start_datetime: new Date().toISOString(),
                End_datetime: new Date().toISOString(),
                Event_Place: 'N/A',
                Max_participant: 0,
                Event_amount: 0,
                Event_mode: 'N/A',
                Event_description: 'No event data available',
                Event_category: 'N/A',
                Event_organizer: 'N/A',
                Event_status: 'N/A',
                registrations: [],
                payments: []
            };
        }

        const { Event, Registrations, Payments } = eventData;

        // Transform registrations with additional fields from API
        const registrations = Registrations?.map((reg, index) => ({
            id: index + 1,
            regId: reg.Reg_id,
            name: reg.Name,
            email: reg.Email,
            phone: reg.Phone || 'N/A',
            college: reg.College_name || 'N/A',
            course: reg.Course || 'N/A',
            department: reg.Department || 'N/A',
            year: reg.Year || 'N/A',
            city: reg.City || 'N/A',
            status: reg.Paid_status === 1 ? 'confirmed' : 'pending',
            registeredAt: reg.Reg_date,
            paymentStatus: reg.Paid_status === 1 ? 'paid' : 'pending',
            ticketNumber: reg.Receipt_No || 'N/A',
            transactionId: reg.Txn_id || 'N/A',
            amountPaid: reg.Txn_amount || 0,
            paymentDate: reg.Txn_date || 'N/A',
            participantId: reg.ParticipantID,
            participantName: reg.ParticipantName
        })) || [];

        // Transform payments
        const payments = Payments?.map((payment, index) => ({
            id: index + 1,
            registrationId: index + 1,
            amount: payment.Txn_amount,
            status: payment.Status === 1 ? 'paid' : payment.Status === 0 ? 'pending' : 'failed',
            paidAt: payment.Status === 1 ? payment.Txn_date : null,
            method: 'Online',
            transactionId: payment.Receipt_No
        })) || [];

        return {
            Event_Id: Event?.Event_Id || eventId,
            Event_Name: Event?.Event_Name || 'Event Not Found',
            Start_datetime: Event?.Start_datetime || new Date().toISOString(),
            End_datetime: Event?.End_datetime || new Date().toISOString(),
            Event_Place: Event?.Event_Place || 'N/A',
            Max_participant: Event?.Max_participant || 0,
            Event_amount: Event?.Event_amount || 0,
            Event_mode: Event?.Event_mode || 'N/A',
            Event_description: Event?.Event_description || 'No event data available',
            Event_category: Event?.Event_category || 'N/A',
            Event_organizer: Event?.Organizer_name || 'N/A',
            Event_status: Event?.Event_status || 'N/A',
            registrations: registrations,
            payments: payments
        };
    }, [eventData, eventId]);

    // Handle row click to show attendee details
    const handleRowClick = async (registration) => {
        try {
            setLoadingAttendee(true);
            setError(null);

            const response = await apiClient.get(`/Event/Admib-Scrn-Reg-details?regId=${registration.regId}`);

            if (!response.data) {
                throw new Error('No data received from server');
            }

            if (response.data.isError) {
                throw new Error(response.data.message);
            }

            if (!response.data.data || response.data.data.length === 0) {
                Swal.fire({
                    title: "No Data",
                    text: "No attendee details found.",
                    icon: "info",
                    confirmButtonText: "OK",
                });
                return;
            }

            // Transform API response to match attendee data structure
            const apiData = response.data.data[0];
            const transformedAttendeeData = {
                name: apiData.Name || registration.name,
                email: apiData.Email || registration.email,
                photo: apiData.PhotoPath_Url || null,
                ticketNumber: apiData.Receipt_No || registration.ticketNumber,
                prefix: apiData.Prefix || 'N/A',
                gender: apiData.Gender === 'M' ? 'Male' : apiData.Gender === 'F' ? 'Female' : 'Other',
                phone: apiData.Phone || registration.phone,
                address: apiData.Ins_Address || 'N/A',
                city: apiData.District?.trim() || registration.city,
                pincode: apiData.Pincode || 'N/A',
                institution: apiData.Inst_Name || registration.college,
                participantType: apiData.Participant || registration.participantType || 'N/A',
                registeredAt: apiData.Reg_date || registration.registeredAt,
                status: registration.status,
                paymentStatus: apiData.PaymentStatus?.toLowerCase() || registration.paymentStatus,
                fullAddress: `${apiData.Ins_Address || ''}, ${apiData.District?.trim() || ''}, ${apiData.State || ''} - ${apiData.Pincode || ''}`.trim(),
                zipCode: apiData.Pincode,
                txn_amount: apiData.txn_amount
            };

            setAttendeeData(transformedAttendeeData);
            setShowAttendeeModal(true);
        } catch (err) {
            console.error('Error fetching attendee data:', err);

            if (err.response?.status === 401) {
                // Session expired - interceptor will handle navigation
                return;
            }

            // If API fails, show basic data from the table row
            const basicAttendeeData = {
                name: registration.name,
                email: registration.email,
                photo: null,
                ticketNumber: registration.ticketNumber,
                prefix: 'N/A',
                gender: 'N/A',
                phone: registration.phone || 'N/A',
                address: 'N/A',
                city: registration.city || 'N/A',
                pincode: 'N/A',
                institution: registration.college || 'N/A',
                participantType: registration.participantType || 'N/A',
                registeredAt: registration.registeredAt,
                status: registration.status,
                paymentStatus: registration.paymentStatus
            };

            setAttendeeData(basicAttendeeData);
            setShowAttendeeModal(true);

            Swal.fire({
                title: "Limited Data",
                text: "Could not fetch complete details. Showing basic information.",
                icon: "info",
                confirmButtonText: "OK",
            });
        } finally {
            setLoadingAttendee(false);
        }
    };

    // Close attendee modal
    const closeAttendeeModal = () => {
        setShowAttendeeModal(false);
        setAttendeeData(null);
    };

    // Calculate comprehensive statistics
    const stats = useMemo(() => {
        const totalRegistrations = transformedEvent.registrations?.length || 0;
        const confirmedRegistrations = transformedEvent.registrations?.filter(r => r.status === 'confirmed').length || 0;
        const pendingRegistrations = transformedEvent.registrations?.filter(r => r.status === 'pending').length || 0;
        const waitingRegistrations = transformedEvent.registrations?.filter(r => r.status === 'waiting').length || 0;

        const totalRevenue = transformedEvent.payments?.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0) || 0;
        const pendingPayments = transformedEvent.payments?.filter(p => p.status === 'pending').length || 0;
        const paidPayments = transformedEvent.payments?.filter(p => p.status === 'paid').length || 0;
        const failedPayments = transformedEvent.payments?.filter(p => p.status === 'failed').length || 0;

        const availableSlots = transformedEvent.Max_participant > 0
            ? Math.max(0, transformedEvent.Max_participant - totalRegistrations)
            : 'Unlimited';
        const registrationRate = transformedEvent.Max_participant > 0
            ? (totalRegistrations / transformedEvent.Max_participant * 100).toFixed(1)
            : 0;

        // Event status calculation
        const now = new Date();
        const startDate = new Date(transformedEvent.Start_datetime);
        const endDate = new Date(transformedEvent.End_datetime);
        let eventStatus = 'upcoming';
        if (now > startDate && now < endDate) eventStatus = 'ongoing';
        if (now > endDate) eventStatus = 'completed';

        return {
            totalRegistrations,
            confirmedRegistrations,
            pendingRegistrations,
            waitingRegistrations,
            totalRevenue,
            pendingPayments,
            paidPayments,
            failedPayments,
            availableSlots,
            registrationRate,
            eventStatus: eventData ? eventStatus : 'not-found'
        };
    }, [transformedEvent, eventData]);

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const handleLogout = () => {
        Swal.fire({
            title: 'Logout?',
            text: 'Are you sure you want to logout?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#dc2626',
            confirmButtonText: 'Yes, logout!',
        }).then((result) => {
            if (result.isConfirmed) {
                document.cookie = "a_t=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
                navigate(`/register/${eventId}/admin`);
            }
        });
    };

    const handlePaymentExport = (format) => {
        if (!transformedEvent || !eventData) {
            Swal.fire({
                title: 'No Data',
                text: 'No event data available to export',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }

        setShowPaymentExportMenu(false);
        downloadPayDetails(format);
    };

    // StatCard Component
    const StatCard = ({ title, value, icon, color, borderColor }) => (
        <div className={`bg-white rounded-xl p-5 shadow-lg border-l-4 ${borderColor} hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                </div>
                <div className={`p-3 rounded-xl ${color}`}>
                    {icon}
                </div>
            </div>
        </div>
    );

    // Status Badge Component
    const StatusBadge = ({ status, type = 'registration' }) => {
        const config = {
            registration: {
                confirmed: { color: 'bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border border-emerald-200', label: 'Confirmed' },
                pending: { color: 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border border-amber-200', label: 'Pending' },
                waiting: { color: 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200', label: 'Waitlist' }
            },
            payment: {
                paid: { color: 'bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border border-emerald-200', label: 'Paid' },
                pending: { color: 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border border-amber-200', label: 'Pending' },
                failed: { color: 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border border-red-200', label: 'Failed' }
            }
        };

        const style = config[type]?.[status] || config.registration.confirmed;

        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${style.color}`}>
                {style.label}
            </span>
        );
    };

    // Attendee Details Modal Component
    const AttendeeDetailsModal = ({ attendee, onClose, isLoading }) => {
        if (!attendee) return null;

        const photoUrl = attendee.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(attendee.name)}&background=4f46e5&color=fff&size=150`;
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-scale-in">
                    {/* Modal Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center ring-2 ring-white/30 overflow-hidden">
                                    <img
                                        src={photoUrl}
                                        alt={attendee.name}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(attendee.name)}&background=4f46e5&color=fff&size=150`;
                                        }}
                                    />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold">{attendee.name}</h2>
                                    <p className="text-blue-100 opacity-90">Attendee Details</p>
                                    <p className="text-sm text-blue-200 opacity-75 mt-1">
                                        ID: {attendee.ticketNumber || 'N/A'} • {attendee.email}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                disabled={isLoading}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Loading State */}
                    {isLoading && (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-2 text-gray-600">Loading attendee details...</p>
                        </div>
                    )}

                    {/* Modal Content */}
                    {!isLoading && (
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Personal Information */}
                                <div className="space-y-6">
                                    {/* Personal Details */}
                                    <div className="bg-gradient-to-br from-blue-50 min-h-[360px] to-indigo-50 rounded-xl p-5">
                                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                            <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            Personal Information
                                        </h3>
                                        <div className="space-y-4">
                                            <DetailItem
                                                label="Prefix"
                                                value={attendee.prefix || 'N/A'}
                                                icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804l-1.414-1.414L8.121 12 3.707 7.586l1.414-1.414L10.95 12l-5.829 5.804z" /></svg>}
                                            />
                                            <DetailItem
                                                label="Full Name"
                                                value={attendee.name}
                                                icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
                                            />
                                            <DetailItem
                                                label="Gender"
                                                value={attendee.gender || 'N/A'}
                                                icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
                                            />
                                            <DetailItem
                                                label="Email Address"
                                                value={attendee.email}
                                                type="email"
                                                icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
                                            />
                                            <DetailItem
                                                label="Phone Number"
                                                value={attendee.phone}
                                                type="phone"
                                                icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>}
                                            />
                                        </div>
                                    </div>

                                    {/* Address Information */}
                                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 min-h-[250px] rounded-xl p-5">
                                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                            <svg className="w-5 h-5 mr-2 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            Address Information
                                        </h3>
                                        <div className="space-y-4">
                                            <DetailItem
                                                label="Address"
                                                value={attendee.address || attendee.fullAddress || 'N/A'}
                                                isLongText={true}
                                                icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>}
                                            />
                                            <DetailItem
                                                label="City"
                                                value={attendee.city || 'N/A'}
                                                icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
                                            />
                                            <DetailItem
                                                label="Pincode"
                                                value={attendee.pincode || attendee.zipCode || 'N/A'}
                                                icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column */}
                                <div className="space-y-6">
                                    {/* Photo & Institution */}
                                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 min-h-[360px] rounded-xl p-5">
                                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                            <svg className="w-5 h-5 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            Institution & Type
                                        </h3>
                                        <div className="space-y-6">
                                            {/* Photo Display */}
                                            <div className="flex flex-col items-center">
                                                <div className="w-32 h-32 mb-4 rounded-xl overflow-hidden border-4 border-white shadow-lg">
                                                    <img
                                                        src={photoUrl}
                                                        alt={attendee.name}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(attendee.name)}&background=4f46e5&color=fff&size=150`;
                                                        }}
                                                    />
                                                </div>
                                                <p className="text-sm text-gray-600">Profile Photo</p>
                                            </div>

                                            <div className="space-y-4">
                                                <DetailItem
                                                    label="Institution"
                                                    value={attendee.institution || attendee.college || 'N/A'}
                                                    isLongText={true}
                                                    icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
                                                />
                                                <DetailItem
                                                    label="Participant Type"
                                                    value={attendee.participantType || 'N/A'}
                                                    icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" /></svg>}
                                                />
                                                <DetailItem
                                                    label="Amount Paid"
                                                    value={attendee.txn_amount || 'N/A'}
                                                    icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" /></svg>}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Registration Details */}
                                    <div className="bg-gradient-to-br min-h-[250px] from-purple-50 to-pink-50 rounded-xl p-5">
                                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                            <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Registration Details
                                        </h3>
                                        <div className="space-y-4">
                                            <DetailItem
                                                label="Registration Date"
                                                value={formatDate(attendee.registeredAt)}
                                                icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
                                            />
                                            <DetailItem
                                                label="Receipt Number"
                                                value={attendee.ticketNumber || 'N/A'}
                                                icon={<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>}
                                            />
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-gray-600">Registration Status:</span>
                                                <StatusBadge status={attendee.status} type="registration" />
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-gray-600">Payment Status:</span>
                                                <StatusBadge status={attendee.paymentStatus} type="payment" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Updated DetailItem component with icons
    const DetailItem = ({ label, value, type = 'text', isLongText = false, icon = null }) => {
        let displayValue = value;

        if (type === 'email' && value !== 'N/A') {
            displayValue = (
                <a href={`mailto:${value}`} className="text-blue-600 hover:text-blue-800 hover:underline flex items-center">
                    <span className="ml-1 overflow-auto">{value}</span>
                </a>
            );
        } else if (type === 'phone' && value !== 'N/A') {
            displayValue = (
                <a href={`tel:${value}`} className="text-blue-600 hover:text-blue-800 hover:underline flex items-center">
                    <span className="ml-1 overflow-auto">{value}</span>
                </a>
            );
        } else {
            displayValue = (
                <div className="flex items-center">
                    <span className={`ml-1 overflow-auto ${isLongText ? 'text-left break-words' : ''}`}>
                        {value}
                    </span>
                </div>
            );
        }

        return (
            <div className="flex items-start justify-between">
                <span className="text-sm font-medium text-gray-600 flex-shrink-0 mr-4">
                    {label}:
                </span>

                <div className="text-right max-w-[200px] text-sm font-semibold text-gray-900 break-words whitespace-normal overflow-hidden">
                    {displayValue}
                </div>
            </div>
        );
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading event data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 flex flex-col min-h-screen">
            {/* Header */}
            <header className="bg-gradient-to-r from-blue-600 to-indigo-700 shadow-xl sticky top-0 z-40">
                <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center ring-2 ring-white/30">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-white">{transformedEvent.Event_Name}</h1>
                                <p className="text-xs text-blue-100">Admin Dashboard</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <button
                                onClick={handleLogout}
                                className="px-4 py-2 text-sm text-white bg-gradient-to-r from-rose-500 to-pink-600 rounded-lg hover:from-rose-600 hover:to-pink-700 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-grow px-4 sm:px-6 lg:px-8 py-6">
                {/* Event Status Banner */}
                <div className="mb-6 bg-gradient-to-r from-blue-700 to-indigo-800 rounded-2xl p-6 text-white shadow-xl">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold mb-2">Welcome, Event Administrator</h2>
                            <p className="text-blue-100">Manage and monitor your event registrations and payments</p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-blue-200 mb-2">Event Status</p>
                            <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium shadow-lg ${!eventData ? 'bg-gradient-to-r from-gray-500 to-gray-700 text-white'
                                : stats.eventStatus === 'ongoing'
                                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white animate-pulse'
                                    : stats.eventStatus === 'upcoming'
                                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
                                        : 'bg-gradient-to-r from-gray-500 to-gray-700 text-white'
                                }`}>
                                {!eventData && '📭 No Data Found'}
                                {eventData && stats.eventStatus === 'ongoing' && ' Live Now'}
                                {eventData && stats.eventStatus === 'upcoming' && ' Upcoming'}
                                {eventData && stats.eventStatus === 'completed' && ' Completed'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <StatCard
                        title="Total Registrations"
                        value={stats.confirmedRegistrations}
                        icon={
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                            </svg>
                        }
                        color="bg-gradient-to-br from-blue-50 to-indigo-100"
                        borderColor="border-l-blue-500"
                    />

                    <StatCard
                        title="Total Collected Amount"
                        value={formatCurrency(stats.totalRevenue)}
                        icon={
                            <div className="w-6 h-6 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg flex items-center justify-center text-white font-bold">
                                ₹
                            </div>
                        }
                        color="bg-gradient-to-br from-emerald-50 to-green-100"
                        borderColor="border-l-emerald-500"
                    />
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Registrations Table */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center space-x-4">
                                    <h3 className="text-xl font-bold text-gray-900">Recent Registrations</h3>
                                    <span className="px-3 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-full text-sm font-medium">
                                        {stats.totalRegistrations} Total
                                    </span>
                                </div>

                                {/* Export Buttons */}
                                <div className="flex items-center space-x-3">
                                    {/* Attendance Export Button */}
                                    <button
                                        onClick={() => handleAttendenceExport("pdf")}
                                        disabled={stats.totalRegistrations === 0 || !eventData}
                                        className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg hover:from-blue-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>Export Attendance</span>
                                    </button>

                                    {/* Payment Export Button with Dropdown */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowPaymentExportMenu(!showPaymentExportMenu)}
                                            disabled={stats.totalRegistrations === 0 || !eventData}
                                            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-green-700 rounded-lg hover:from-emerald-700 hover:to-green-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span>₹ Export Payment</span>
                                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {/* Dropdown Menu */}
                                        {showPaymentExportMenu && (
                                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
                                                <button
                                                    onClick={() => handlePaymentExport('pdf')}
                                                    disabled={!eventData}
                                                    className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gradient-to-r hover:from-red-50 hover:to-rose-100 hover:text-red-700 transition-colors duration-200 border-b border-gray-100 flex items-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <div className="w-8 h-8 bg-gradient-to-r from-red-100 to-rose-200 rounded-lg flex items-center justify-center">
                                                        <span className="text-red-600 font-bold text-xs">PDF</span>
                                                    </div>
                                                    <span>Export as PDF</span>
                                                </button>
                                                <button
                                                    onClick={() => handlePaymentExport('excel')}
                                                    disabled={!eventData}
                                                    className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-green-100 hover:text-emerald-700 transition-colors duration-200 border-b border-gray-100 flex items-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <div className="w-8 h-8 bg-gradient-to-r from-emerald-100 to-green-200 rounded-lg flex items-center justify-center">
                                                        <span className="text-emerald-600 font-bold text-xs">XLS</span>
                                                    </div>
                                                    <span>Export as Excel</span>
                                                </button>
                                                <button
                                                    onClick={() => handlePaymentExport('cse')}
                                                    disabled={!eventData}
                                                    className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-100 hover:text-blue-700 transition-colors duration-200 flex items-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <div className="w-8 h-8 bg-gradient-to-r from-blue-100 to-indigo-200 rounded-lg flex items-center justify-center">
                                                        <span className="text-blue-600 font-bold text-xs">CSV</span>
                                                    </div>
                                                    <span>Export as CSV</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="overflow-x-auto h-[calc(100vh-220px)]">
                                <table className="w-full">
                                    <thead>
                                        <tr className="text-left text-sm text-gray-600 border-b">
                                            <th className="pb-3 px-2 font-semibold">Attendee</th>
                                            <th className="pb-3 px-2 font-semibold">Email</th>
                                            <th className="pb-3 px-2 font-semibold">Status</th>
                                            <th className="pb-3 px-2 font-semibold">Payment</th>
                                            <th className="pb-3 px-2 font-semibold">Date</th>
                                            <th className="pb-3 px-2 font-semibold">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {transformedEvent.registrations.length > 0 ? (
                                            transformedEvent.registrations.map((reg) => (
                                                <tr
                                                    key={reg.id}
                                                    onClick={() => handleRowClick(reg)}
                                                    className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 transition-all duration-200 cursor-pointer group"
                                                >
                                                    <td className="py-4 px-2">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-medium shadow-md group-hover:scale-110 transition-transform duration-300">
                                                                {reg.name?.charAt(0) || 'N'}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                                                                    {reg.name || 'No Name'}
                                                                </p>
                                                                <p className="text-xs text-gray-500">{reg.ticketNumber || 'No Ticket'}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-2">
                                                        <p className="text-sm text-gray-700 truncate max-w-[200px]">{reg.email || 'No Email'}</p>
                                                    </td>
                                                    <td className="py-4 px-2">
                                                        <StatusBadge status={reg.status} type="registration" />
                                                    </td>
                                                    <td className="py-4 px-2">
                                                        <StatusBadge status={reg.paymentStatus} type="payment" />
                                                    </td>
                                                    <td className="py-4 px-2">
                                                        <p className="text-sm text-gray-600">
                                                            {reg.registeredAt ? new Date(reg.registeredAt).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            }) : 'N/A'}
                                                        </p>
                                                    </td>
                                                    <td className="py-4 px-2">
                                                        <div className="flex items-center justify-center">
                                                            <button
                                                                onClick={(e) => handleIndividualDownload(e, reg, 'pdf')}
                                                                disabled={downloadingId === reg.regId}
                                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                title="Download PDF"
                                                            >
                                                                {downloadingId === reg.regId ? (
                                                                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                                                ) : (
                                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                    </svg>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="py-8 text-center">
                                                    <div className="text-gray-400">
                                                        <svg className="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                                        </svg>
                                                        <p className="text-lg font-medium text-gray-500">
                                                            {eventData ? 'No registrations yet' : 'No event data available'}
                                                        </p>
                                                        <p className="text-sm text-gray-400">
                                                            {eventData ? 'Attendees will appear here once they register' : 'Event information not found'}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Event Details Sidebar */}
                    <div className="space-y-6">
                        {/* Event Info Card */}
                        <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl p-6 shadow-xl border border-gray-100">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">Event Details</h3>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Date:</span>
                                    <span className="text-sm font-semibold bg-gradient-to-r from-blue-100 to-indigo-100 px-3 py-1 rounded-lg">
                                        {formatDate(transformedEvent.Start_datetime)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Venue:</span>
                                    <span className="text-sm font-semibold text-right max-w-[150px] bg-gradient-to-r from-blue-100 to-indigo-100 px-3 py-1 rounded-lg">
                                        {transformedEvent.Event_Place}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Total Registrations:</span>
                                    <span className="text-sm font-semibold text-right max-w-[150px] bg-gradient-to-r from-blue-100 to-indigo-100 px-3 py-1 rounded-lg">
                                        {stats.confirmedRegistrations}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Payments Summary */}
                        <div className="bg-gradient-to-br from-white to-emerald-50 rounded-2xl p-6 shadow-xl border border-gray-100">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">Payments Summary</h3>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Paid:</span>
                                    <span className="text-sm font-bold bg-gradient-to-r from-emerald-100 to-green-100 px-3 py-1 rounded-lg">
                                        {stats.paidPayments} Payments
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Pending:</span>
                                    <span className="text-sm font-bold bg-gradient-to-r from-amber-100 to-yellow-100 px-3 py-1 rounded-lg">
                                        {stats.pendingPayments} Payments
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Failed:</span>
                                    <span className="text-sm font-bold bg-gradient-to-r from-rose-100 to-red-100 px-3 py-1 rounded-lg">
                                        {stats.failedPayments} Payments
                                    </span>
                                </div>
                                <div className="pt-6 border-t">
                                    <div className="text-center bg-gradient-to-r from-emerald-50 to-green-50 p-4 rounded-xl">
                                        <p className="text-xs text-gray-600 mb-2">Total Collected</p>
                                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</p>
                                        <div className="mt-2 px-3 py-1 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-full text-xs inline-block">
                                            {stats.paidPayments} Successful Transactions
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Sticky Footer */}
            <footer className="bg-blue-700 text-white shadow-2xl border-t border-gray-800">
                <div className="text-center p-2">
                    © 2026 All rights reserved PSG Software Technologies
                </div>
            </footer>

            {/* Attendee Details Modal */}
            {showAttendeeModal && (
                <AttendeeDetailsModal
                    attendee={attendeeData}
                    onClose={closeAttendeeModal}
                    isLoading={loadingAttendee}
                />
            )}
        </div>
    );
};

export default EventAdminDashboard;